import React, { useEffect, useState } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { collection, doc, getDoc, getDocs, setDoc, updateDoc } from 'firebase/firestore';
import { db, shortify } from '../../firebase';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  ExclamationCircleIcon,
  SearchIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon,
} from '@heroicons/react/outline';
import Tooltip from '../../components/Tooltip';
import BigIdDropdown from '../../components/BigIdDropdown';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';

function ImportCsv({ selectedCompany }) {
  const [selectedYear, setSelectedYear] = useState();

  const [years, setYears] = useState([]);
  const [yearsVisible, setYearsVisible] = useState();

  const [csvData, setCsvData] = useState();
  const [csvFile, setCsvFile] = useState();

  const [formData, setFormData] = useState();
  const [userData, setUserData] = useState();
  const [userDataArray, setUserDataArray] = useState([]);
  const [clientNames, setClientNames] = useState([]);

  const [displayIndex, setDisplayIndex] = useState();
  const [csvSettingsOpen, setCsvSettingsOpen] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showOnlyChanges, setShowOnlyChanges] = useState(true);
  const [previewQuery, setPreviewQuery] = useState('');

  // Import options
  const [importOptions, setImportOptions] = useState(() => {
    try {
      return (
        JSON.parse(localStorage.getItem('importCsvOptions')) || {
          delimiter: 'auto', // auto | , | ; | \t
          convertLiteralNewlines: true,
          applyBlankCellsClear: false,
        }
      );
    } catch (e) {
      return {
        delimiter: 'auto',
        convertLiteralNewlines: true,
        applyBlankCellsClear: false,
      };
    }
  });
  useEffect(() => {
    localStorage.setItem('importCsvOptions', JSON.stringify(importOptions));
  }, [importOptions]);

  /// Title Specification ///

  useEffect(() => {
    document.title = 'Import CSV - Add or Recover Data from CSV-Tables';
  }, []);

  /// Loading available Document Types ///

  useEffect(() => {
    async function fetchYears() {
      const newYears = await getDocs(collection(db, 'user_data', selectedCompany.id, 'years'));
      setYears(newYears.docs);
    }

    fetchYears();
  }, [selectedCompany]);

  /// Document Type Selection ///

  function selectYear(year) {
    setYearsVisible(false);
    setSelectedYear(year);
  }

  /// Handling CSV Entry ///

  async function uploadCsv(e) {
    if (e.target.files[0]) {
      setCsvFile(e.target.files[0]);
    }
  }

  function formatNumber(number) {
    if (number === null || number === undefined) {
      return '0';
    }
    let s = number.toString();
    // Remove commas as thousands
    s = s.replace(/,/g, '');
    return s || '0';
    // return parseFloat(number.toString().replace(/,/g , '')).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "0"
  }

  /// CSV Parsing ///

  async function parseCsv() {
    // Route to XLSX parser if user selected an Excel file
    if (csvFile && /\.(xlsx|xls)$/i.test(csvFile.name)) {
      await parseXlsx();
      return;
    }
    setUserDataArray([]);

    const delimiterOpt =
      importOptions.delimiter === 'auto' ? undefined : importOptions.delimiter;

    Papa.parse(csvFile, {
      header: true,
      delimiter: delimiterOpt,
      // Be tolerant with empty lines inside quoted cells
      skipEmptyLines: 'greedy',
      quoteChar: '"',
      escapeChar: '"',
      encoding: 'utf-8',
      transformHeader: (h) =>
        (h || '')
          .replace(/^\uFEFF/, '')
          .replace(/\s+/g, ' ')
          .trim(),
      complete: function (results) {
        // Normalize line breaks and allow users to type literal \n to mean a newline
        const processed = results.data.map((row) => {
          const out = {};
          for (const [key, val] of Object.entries(row)) {
            if (typeof val === 'string') {
              let text = val
                // Normalize CRLF/CR to LF
                .replace(/\r\n/g, '\n')
                .replace(/\r/g, '\n');
              if (importOptions.convertLiteralNewlines) {
                text = text.replace(/\\n/g, '\n');
              }
              out[key] = text;
            } else {
              out[key] = val;
            }
          }
          return out;
        });
        setCsvData(processed);
      },
    });
  }

  async function parseXlsx() {
    try {
      const ab = await csvFile.arrayBuffer();
      const wb = XLSX.read(ab, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: '' });
      // Normalize headers like in CSV path
      const processed = json.map((row) => {
        const out = {};
        for (const [k, v] of Object.entries(row)) {
          const key = (k || '').replace(/^\uFEFF/, '').trim();
          if (typeof v === 'string') {
            let text = v.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
            if (importOptions.convertLiteralNewlines) text = text.replace(/\\n/g, '\n');
            out[key] = text;
          } else {
            out[key] = v;
          }
        }
        return out;
      });
      setCsvData(processed);
    } catch (e) {
      console.error('Failed to parse XLSX:', e);
      toast.error('Failed to parse XLSX file');
    }
  }

  // Helper: robustly extract Document ID from a CSV/XLSX row
  function getDocIdFromRow(row) {
    if (!row || typeof row !== 'object') return undefined;
    for (const key of Object.keys(row)) {
      if (typeof key !== 'string') continue;
      const norm = key.replace(/^\uFEFF/, '').trim().toLowerCase();
      if (norm === 'document') {
        const val = row[key];
        if (val === undefined || val === null) return undefined;
        return typeof val === 'string' ? val.trim() : String(val);
      }
    }
    return undefined;
  }

  // Build preview of changes before writing
  async function buildPreview() {
    if (!selectedYear?.id || !formData || !csvData || userDataArray.length === 0) return;
    setPreviewLoading(true);
    const results = [];

    // Build quick lookup for field metadata by id
    const fieldMeta = new Map();
    for (const category of formData.categories || []) {
      for (const field of category.fields || []) {
        fieldMeta.set(field.id, { topic: field.topic, type: category.type });
      }
    }

    for (const [index, row] of Object.entries(csvData)) {
      const docId = getDocIdFromRow(row);
      if (!docId) continue;
      let existentSubsidiary;
      try {
        existentSubsidiary = await getDoc(
          doc(
            db,
            'user_data',
            selectedCompany.id,
            'years',
            selectedYear.id,
            'subsidaries',
            docId
          )
        );
      } catch (e) {
        existentSubsidiary = undefined;
      }

      const existingValues =
        existentSubsidiary && existentSubsidiary.exists()
          ? existentSubsidiary.data()?.fieldValues || []
          : [];
      const newValues = userDataArray[index] || [];

      for (const newField of newValues) {
        const meta = fieldMeta.get(newField.id);
        if (!meta) continue;

        const oldField = existingValues.find((fv) => fv.id === newField.id);
        const isInfo = meta.type === 'information';
        const oldValRaw = isInfo ? oldField?.answer || '' : oldField?.transaction_amount || '';
        const newValRaw = isInfo ? newField?.answer || '' : newField?.transaction_amount || '';

        let status = 'unchanged';
        if (!existentSubsidiary || !existentSubsidiary.exists()) {
          status = 'create';
        } else if (!oldField && newValRaw !== '') {
          status = 'add';
        } else if (oldField) {
          if (isInfo) {
            const norm = (s) => (s || '').toString().replace(/\r\n/g, '\n').trim();
            const oldEmpty = norm(oldValRaw) === '';
            const newEmpty = norm(newValRaw) === '';
            if (importOptions.applyBlankCellsClear && newEmpty && !oldEmpty) {
              status = 'delete';
            } else if (!importOptions.applyBlankCellsClear && newEmpty) {
              status = 'unchanged';
            } else if (oldEmpty && !newEmpty) {
              status = 'add';
            } else if (norm(oldValRaw) !== norm(newValRaw)) {
              status = 'update';
            }
          } else {
            const parseNum = (s) => {
              if (s === null || s === undefined) return null;
              let x = s.toString().trim();
              if (x === '') return null;
              // Remove commas as thousands and spaces
              x = x.replace(/,/g, '').replace(/\s/g, '');
              const n = Number(x);
              return Number.isFinite(n) ? n : null;
            };
            const a = parseNum(oldValRaw);
            const b = parseNum(newValRaw);
            if (b === null) {
              if (importOptions.applyBlankCellsClear && a !== null) {
                status = 'delete';
              } else {
                status = 'unchanged';
              }
            } else if (a === null && b !== null) {
              status = 'add';
            } else if (a !== null && b !== null) {
              // Compare using the same precision we display (2 decimals) to avoid
              // flagging tiny floating-point differences as updates.
              const precision = 2;
              const factor = Math.pow(10, precision);
              const ra = Math.round(a * factor) / factor;
              const rb = Math.round(b * factor) / factor;
              if (ra !== rb) status = 'update';
            }
          }
        }

        // Format values consistently for preview (2 decimals for numeric fields)
        const formatPreview = (num, raw) => {
          if (isInfo) return raw;
          if (num === null || num === undefined) return '';
          let s = Number(num).toFixed(2);
          return s;
        };

        const oldNum = meta.type !== 'information' ? (() => {
          let x = oldValRaw;
          let t = x.toString().replace(/,/g, '');
          const n = Number(t);
          return Number.isFinite(n) ? n : null;
        })() : null;

        const newNum = meta.type !== 'information' ? (() => {
          let x = newValRaw;
          let t = x.toString().replace(/,/g, '');
          const n = Number(t);
          return Number.isFinite(n) ? n : null;
        })() : null;

        results.push({
          docId,
          fieldId: newField.id,
          topic: meta.topic,
          type: meta.type,
          oldValue: isInfo ? oldValRaw : formatPreview(oldNum, oldValRaw),
          newValue: isInfo ? newValRaw : formatPreview(newNum, newValRaw),
          status,
        });
      }
    }

    setPreviewRows(results);
    setPreviewLoading(false);
  }

  useEffect(() => {
    // Passing CSV Data in selected year form data
    // Problem: What if no form existent? -> Option for form creation

    async function fetchUserDataArray() {
      if (selectedYear?.id) {
        const form = await getDoc(
          doc(
            db,
            'user_data',
            selectedCompany.id,
            'years',
            selectedYear.id,
            'forms',
            'standard_form'
          )
        );
        setFormData(form.data());

        const tempFormData = form.data();

        const newUserDataArray = [];
        const newClientNames = [];

        for (const row of csvData) {
          const docId = getDocIdFromRow(row);
          newClientNames.push(docId);

          const newFieldValues = [];

          for (const category of tempFormData.categories) {
            for (const field of category.fields) {
              // Pull raw value from CSV/XLSX row
              const raw = row[field.topic];
              const isBlank =
                raw === undefined ||
                raw === null ||
                (typeof raw === 'string' && raw.trim() === '');
              const hasValue = !isBlank;

              // Only include if there is a value, OR if blanks should actively clear (and header exists)
              if (hasValue || (importOptions.applyBlankCellsClear && raw !== undefined)) {
                if (category.type === 'information') {
                  newFieldValues.push({
                    answer: hasValue ? (typeof raw === 'string' ? raw.trim() : raw) : '',
                    id: field.id,
                    comment: '',
                    done: false,
                  });
                } else if (
                  category.type === 'standard-transactions' ||
                  category.type === 'numbers'
                ) {
                  newFieldValues.push({
                    transaction_amount: hasValue ? formatNumber(raw) : '',
                    id: field.id,
                    comment: '',
                    done: false,
                  });
                }
              }
            }
          }

          newUserDataArray.push(newFieldValues);
        }

        setUserDataArray(newUserDataArray);
        setClientNames(newClientNames);
      }
    }

    fetchUserDataArray();
  }, [csvData, importOptions.applyBlankCellsClear]);

  useEffect(() => {
    setDisplayIndex(0);
    setUserData(userDataArray[0]);
  }, [userDataArray]);

  /// Switching between Documents ///

  function nextRow() {
    if (userDataArray[displayIndex + 1]) {
      setDisplayIndex(displayIndex + 1);
      setUserData(userDataArray[displayIndex + 1]);
    }
  }

  function previousRow() {
    if (userDataArray[displayIndex - 1]) {
      setDisplayIndex(displayIndex - 1);
      setUserData(userDataArray[displayIndex - 1]);
    }
  }

  /// Saving CSV Data ///

  const [showWarning, setShowWarning] = useState(false);
  const [overwriteClients, setOverwriteClients] = useState();

  const [writePercentage, setWritePercentage] = useState();

  // Modified handleWrite function for src/pages/ImportCsv/ImportCsv.js

  async function handleWrite() {
    setWritePercentage(0);

    // Basic auth check
    const auth = getAuth();
    const currentUser = auth.currentUser;
    console.log('Current user:', currentUser ? currentUser.uid : 'Not authenticated');

    // Check if user is a master user according to the rules
    try {
      const masterUsersDoc = await getDoc(doc(db, 'master_users', 'user_roles'));
      if (masterUsersDoc.exists()) {
        const masterUsersData = masterUsersDoc.data();
        const isMasterEditor =
          currentUser &&
          masterUsersData.roles &&
          masterUsersData.roles[currentUser.uid] === 'master-editor';

        // If not a master user, show alert and return
        if (!isMasterEditor) {
          alert('You must be a master user to perform this operation.');
          setWritePercentage();
          return;
        }
      }
    } catch (authError) {
      console.error('Error checking master user status:', authError);
    }

    console.log('Selected company:', selectedCompany?.id);
    console.log('Selected year:', selectedYear?.id);

    if (!selectedCompany?.id || !selectedYear?.id) {
      toast.error('Please select a company and a year before importing.');
      setWritePercentage();
      return;
    }

    // Preflight: ensure CSV has a usable Document column
    const hasAnyDocId = Array.isArray(csvData)
      ? csvData.some((row) => !!getDocIdFromRow(row))
      : false;
    if (!hasAnyDocId) {
      toast.error("CSV is missing a 'Document' column or all rows are blank.");
      setWritePercentage();
      return;
    }

    const newOverwriteClients = [];

    try {
      for (const [index, row] of Object.entries(csvData)) {
        const docId = getDocIdFromRow(row);
        if (!docId) {
          console.warn(`Skipping row ${index} without a Document value.`);
          // Update progress even when skipping
          setWritePercentage(
            Object.entries(csvData).length > 1
              ? ((parseInt(index, 10) / (Object.entries(csvData).length - 1)) * 100).toFixed()
              : '100'
          );
          continue;
        }
        try {
          console.log(`Processing document: ${docId}`);

          // First, try to get the existing document
          // We need this to properly merge values
          try {
            const existentSubsidiary = await getDoc(
              doc(
                db,
                'user_data',
                selectedCompany.id,
                'years',
                selectedYear.id,
                'subsidaries',
                docId
              )
            );

            // If document exists, prepare for proper merge
            if (existentSubsidiary.exists() && existentSubsidiary.data()?.fieldValues) {
              console.log(`Document ${docId} exists, preparing for proper merge`);
              const existentSubsidiaryData = existentSubsidiary.data();
              const newFieldValueIDs = userDataArray[index].map((fieldValue) => fieldValue.id);

              // Filtering all values from existent client that are not in the newly imported CSV
              const filteredData = existentSubsidiaryData.fieldValues.filter(
                (fieldValue) => !newFieldValueIDs.includes(fieldValue.id)
              );

              // Adding all newly imported values
              for (const fieldValue of userDataArray[index]) {
                filteredData.push(fieldValue);
              }

              newOverwriteClients.push({ id: docId, data: filteredData });
            } else {
              // Document doesn't exist, create new
              console.log(`Creating new document for ${docId}`);
              const docData = {
                fieldValues: userDataArray[index],
                roles: {},
                accessGroups: [], // Required by security rules
              };

              try {
                await setDoc(
                  doc(
                    db,
                    'user_data',
                    selectedCompany.id,
                    'years',
                    selectedYear.id,
                    'subsidaries',
                    docId
                  ),
                  docData
                );
                console.log(`Successfully created new subsidiary ${docId}`);
                toast.success(`Created new subsidiary: ${docId}`);
              } catch (createError) {
                console.error(`Error creating new subsidiary ${docId}:`, createError);
                toast.error(`Failed to create ${docId}: ${createError.message}`);
              }
            }
          } catch (readError) {
            // If we can't read the document (permission error), try direct create
            console.log(`Could not read document ${docId}, attempting direct create`);

            const docData = {
              fieldValues: userDataArray[index],
              roles: {},
              accessGroups: [], // Required by security rules
            };

            try {
              await setDoc(
                doc(
                  db,
                  'user_data',
                  selectedCompany.id,
                  'years',
                  selectedYear.id,
                  'subsidaries',
                  docId
                ),
                docData
              );
              console.log(`Successfully created subsidiary ${docId} with direct method`);
              toast.success(`Created subsidiary: ${docId}`);
            } catch (directError) {
              console.error(`Error with direct create for ${docId}:`, directError);
              toast.error(`Failed to create ${docId}: ${directError.message}`);
            }
          }

          // Update progress
          setWritePercentage(
            Object.entries(csvData).length > 1
              ? ((parseInt(index, 10) / (Object.entries(csvData).length - 1)) * 100).toFixed()
              : '100'
          );
        } catch (error) {
          console.error(`Error processing row for ${docId}:`, error);
          // Continue to next row even if one fails
        }
      }

      // Handle overwrite confirmations if needed
      if (newOverwriteClients.length > 0) {
        console.log(`${newOverwriteClients.length} clients need overwriting, showing confirmation`);
        setShowWarning(true);
        setOverwriteClients(newOverwriteClients);
      } else {
        console.log('Import completed with no overwrites needed');
        toast.success('CSV import completed successfully');
        setWritePercentage();
      }
    } catch (error) {
      console.error('Error in handleWrite:', error);
      toast.error('An error occurred while processing the CSV data');
      setWritePercentage();
    }
  }

  // Clean up overwrite function as well
  async function overwrite() {
    setShowWarning(false);
    console.log('Starting overwrite process for', overwriteClients.length, 'clients');

    try {
      for (const overwriteClient of overwriteClients) {
        console.log(`Updating data for client: ${overwriteClient.id}`);
        try {
          await updateDoc(
            doc(
              db,
              'user_data',
              selectedCompany.id,
              'years',
              selectedYear.id,
              'subsidaries',
              overwriteClient.id
            ),
            {
              fieldValues: overwriteClient.data,
              accessGroups: [], // Required by security rules
            }
          );
          console.log(`Successfully updated data for ${overwriteClient.id}`);
          toast.success(`Updated data for ${overwriteClient.id}`);
        } catch (clientError) {
          console.error(`Error updating client ${overwriteClient.id}:`, clientError);
          toast.error(`Failed to update ${overwriteClient.id}: ${clientError.message}`);
        }
      }
      console.log('Overwrite process completed');
      toast.success('All overwrites completed successfully');
    } catch (error) {
      console.error('Error during overwrite:', error);
      toast.error('An error occurred while overwriting existing data');
    }

    setWritePercentage();
  }

  async function cancelOverwrite() {
    setShowWarning(false);
    setWritePercentage();
  }

  /// HTML Component ///

  const isExcel = !!(csvFile && /\.(xlsx|xls)$/i.test(csvFile.name || ''));
  const parseBtnText = !csvFile ? 'Parse File' : isExcel ? 'Parse Excel' : 'Parse CSV';

  // Export the current preview (respecting filters) to Excel
  function exportPreviewToExcel() {
    try {
      const filtered = previewRows
        .filter((r) =>
          showOnlyChanges ? ['create', 'add', 'update', 'delete'].includes(r.status) : true
        )
        .filter((r) =>
          previewQuery
            ? (r.docId + ' ' + r.topic).toLowerCase().includes(previewQuery.toLowerCase())
            : true
        );

      if (filtered.length === 0) {
        toast.info('Nothing to export with the current filters');
        return;
      }

      const rows = filtered.map((r) => ({
        Document: r.docId,
        Field: r.topic,
        Type: r.type,
        OldValue: r.oldValue,
        NewValue: r.newValue,
        Status: r.status,
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Diff');

      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const fname = `import-diff-${selectedCompany?.id || 'company'}-${
        selectedYear?.id || 'year'
      }-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(
        now.getHours()
      )}${pad(now.getMinutes())}${pad(now.getSeconds())}.xlsx`;
      XLSX.writeFile(wb, fname);
      toast.success('Exported diff as Excel');
    } catch (e) {
      console.error('Export failed:', e);
      toast.error('Failed to export diff');
    }
  }

  return (
    <>
      <div className="rounded-lg bg-white p-3 m-10 font-body mx-auto w-[90%] max-w-[64rem] shadow-lg">
        <div className="flex items-center gap-3">
          <div className="pl-1">
            <DownloadIcon className="w-12 h-12 bg-indigo-400 text-white p-2 m-1 rounded-full" />
          </div>
          <div>
            <p className="text-2xl p-1 m-1">Import Data (CSV or Excel)</p>
            {csvFile && (
              <p className="text-xs text-slate-500 -mt-2 ml-2">
                {isExcel ? 'Detected file type: Excel (.xlsx/.xls)' : 'Detected file type: CSV'}
              </p>
            )}
          </div>
        </div>

        {/* Top controls row */}
        <div className="mt-3 flex items-center justify-start gap-3 flex-wrap">
          <div className="min-w-[12rem]">
              <BigIdDropdown
                width={'100%'}
                placeholder={'Document Type'}
                color={'indigo'}
                options={years}
                dropdownVisible={yearsVisible}
                setDropdownVisible={setYearsVisible}
                selectFunction={selectYear}
                selectedOption={selectedYear}
              />
            </div>
          <div className="flex items-center gap-3">
            <label
              htmlFor="csv-upload"
              className="h-max whitespace-nowrap rounded-lg text-white bg-indigo-500 hover:bg-indigo-600 transition px-6 py-2 cursor-pointer text-center"
            >
              {csvFile ? shortify(csvFile.name, 20) : 'Browse CSV or Excel'}
            </label>
            <input id="csv-upload" className="hidden" type="file" onChange={(e) => uploadCsv(e)} />
            <button
              className="h-max px-6 py-2 rounded-lg text-white bg-indigo-500 whitespace-nowrap"
              onClick={parseCsv}
            >
              {parseBtnText}
            </button>
            <button
              className="h-max px-6 py-2 rounded-lg text-white bg-emerald-500 whitespace-nowrap disabled:opacity-50"
              disabled={!csvData || !formData || userDataArray.length === 0 || previewLoading}
              onClick={buildPreview}
            >
              {previewLoading ? 'Preparing…' : 'Review Changes'}
            </button>
          </div>
        </div>

        {/* Global import option (applies to CSV and Excel) */}
        <div className="mt-2 text-sm flex items-center gap-3">
          <label htmlFor="opt-clear-blanks-global" className="flex items-center gap-2">
            <input
              id="opt-clear-blanks-global"
              type="checkbox"
              checked={importOptions.applyBlankCellsClear}
              onChange={(e) =>
                setImportOptions({ ...importOptions, applyBlankCellsClear: e.target.checked })
              }
            />
            Blank cells overwrite existing values
          </label>
          <Tooltip content="When enabled, empty cells in the file will clear the existing value for that field. Applies to CSV and Excel.">
            <InformationCircleIcon className="w-4 h-4 text-slate-400" />
          </Tooltip>
        </div>

        {/* CSV settings collapsible: only when CSV selected */}
        {!isExcel && csvFile && (
          <div className="w-[100%] mt-3">
            <button
              className="w-[100%] flex items-center justify-between px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all"
              onClick={() => setCsvSettingsOpen((v) => !v)}
            >
              <span className="text-sm font-medium">CSV Settings</span>
              {csvSettingsOpen ? (
                <ChevronUpIcon className="w-5 h-5" />
              ) : (
                <ChevronDownIcon className="w-5 h-5" />
              )}
            </button>
            {csvSettingsOpen && (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                <div className="text-xs text-slate-600 mb-3">
                  These options affect CSV parsing only. Excel files ignore these settings.
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <label className="min-w-[5.5rem]">Delimiter</label>
                    <Tooltip content="Choose the separator character used in your CSV. Auto detects ; , or Tab from the first lines. If your numbers use comma as decimal, Semicolon is often the right choice.">
                      <InformationCircleIcon className="w-4 h-4 text-slate-400" />
                    </Tooltip>
                    <select
                      value={importOptions.delimiter}
                      onChange={(e) =>
                        setImportOptions({ ...importOptions, delimiter: e.target.value })
                      }
                      className="border rounded p-1"
                    >
                      <option value="auto">Auto</option>
                      <option value=",">Comma</option>
                      <option value=";">Semicolon</option>
                      <option value="\t">Tab</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      id="opt-convert-newlines"
                      type="checkbox"
                      checked={importOptions.convertLiteralNewlines}
                      onChange={(e) =>
                        setImportOptions({
                          ...importOptions,
                          convertLiteralNewlines: e.target.checked,
                        })
                      }
                    />
                    <label htmlFor="opt-convert-newlines">{"Convert \"\\n\" to newline"}</label>
                    <Tooltip content={'Turn the literal \\n text into a real line break inside cells. Quoted multi-line cells are already supported.'}>
                      <InformationCircleIcon className="w-4 h-4 text-slate-400" />
                    </Tooltip>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Preview Changes Table */}
      {previewRows.length > 0 && (
        <div className="mt-5 font-body rounded-lg bg-white w-[85%] mx-auto p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xl">Preview Changes</p>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showOnlyChanges}
                  onChange={(e) => setShowOnlyChanges(e.target.checked)}
                />
                Show only changes
              </label>
              <input
                className="border rounded p-1 text-sm"
                placeholder="Search document or field"
                value={previewQuery}
                onChange={(e) => setPreviewQuery(e.target.value)}
              />
              <button
                className="h-max px-4 py-2 rounded-lg text-white bg-slate-600 hover:bg-slate-700 whitespace-nowrap disabled:opacity-50"
                onClick={exportPreviewToExcel}
                disabled={previewRows.length === 0}
              >
                Export Diff (Excel)
              </button>
            </div>
          </div>
          <div className="border rounded max-h-[60vh] overflow-y-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 sticky top-0 z-10">
                <tr>
                  <th className="text-left p-2 border-b">Document</th>
                  <th className="text-left p-2 border-b">Field</th>
                  <th className="text-left p-2 border-b">Old value</th>
                  <th className="text-left p-2 border-b">New value</th>
                  <th className="text-left p-2 border-b">Status</th>
                </tr>
              </thead>
              <tbody>
                {previewRows
                  .filter((r) =>
                    showOnlyChanges
                      ? ['create', 'add', 'update', 'delete'].includes(r.status)
                      : true
                  )
                  .filter((r) =>
                    previewQuery
                      ? (r.docId + ' ' + r.topic)
                          .toLowerCase()
                          .includes(previewQuery.toLowerCase())
                      : true
                  )
                  .map((r, idx) => (
                    <tr key={idx} className="odd:bg-white even:bg-gray-50">
                      <td className="p-2 align-top border-b">{r.docId}</td>
                      <td className="p-2 align-top border-b">{r.topic}</td>
                      <td className="p-2 align-top border-b max-w-[24rem] whitespace-pre-wrap">{r.oldValue}</td>
                      <td className="p-2 align-top border-b max-w-[24rem] whitespace-pre-wrap">{r.newValue}</td>
                      <td className="p-2 align-top border-b">
                        <span
                          className={
                            'px-2 py-0.5 rounded text-xs ' +
                            (r.status === 'update'
                              ? 'bg-yellow-100 text-yellow-800'
                              : r.status === 'add'
                              ? 'bg-blue-100 text-blue-800'
                              : r.status === 'create'
                              ? 'bg-emerald-100 text-emerald-800'
                              : r.status === 'delete'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-700')
                          }
                        >
                          {r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div
        className={
          'mt-5 font-body rounded-lg bg-white w-[85%] mx-auto p-4' + (userData ? '' : ' hidden')
        }
      >
        <div className="flex items-center justify-between relative">
          <div className="flex items-center my-2 p-1 w-max">
            <button
              onClick={previousRow}
              className="py-2 px-5 bg-blue-400 text-white mr-6 shadow-lg rounded-lg"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>

            <div className="mx-4">{clientNames[displayIndex]}</div>

            <button
              onClick={nextRow}
              className="py-2 px-5 bg-blue-400 text-white ml-6 shadow-lg rounded-lg"
            >
              <ChevronRightIcon className="w-6 h-6" />
            </button>
          </div>
          <div className="border rounded-lg p-1 text-xl flex items-center absolute left-0 right-0 mx-auto w-max">
            <input className="focus:outline-none " />
            <SearchIcon className="w-5 h-5 inline mx-1" />
          </div>
          <div className="relative">
            <button
              onClick={handleWrite}
              className="w-max bg-blue-400 text-white px-10 py-2 shadow-lg hover:bg-blue-600 w-36 transition-all rounded-lg"
            >
              {writePercentage ? writePercentage + '%' : 'Write'}
            </button>
            <div
              className={
                'absolute text-center mt-1 bg-white p-6 rounded-lg w-72 shadow-basic right-0 text-red-500 ' +
                (showWarning ? '' : 'hidden')
              }
            >
              <ExclamationCircleIcon className="w-12 h-12 mx-auto mb-3" />
              <p className="text-2xl">Warning!</p>
              <p className="">Some subsidiaries already exist. Overwrite?</p>
              <div className="mt-4 flex justify-between">
                <button
                  onClick={cancelOverwrite}
                  className="w-[50%] p-1 bg-red-500 rounded-lg text-white mr-2 shadow-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={overwrite}
                  className="w-[50%] p-1 rounded-lg text-red shadow-lg border border-red-500 hover:bg-red-500 hover:text-white transition-all"
                >
                  Overwrite
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col">
          <div className="w-[100%] my-[2px] text-lg py-2 border-b bg-slate-300">
            <div className="w-[95%] px-5 mx-auto flex">
              <div className="w-[25%] flex justify-center items-center">Topic</div>
              <div className="w-[25%] flex justify-center items-center">Description</div>
              <div className="w-[25%] flex justify-center items-center">Enter in English</div>
              <div className="w-[25%] flex justify-center items-center">Remarks</div>
            </div>
          </div>

          {formData?.categories?.map((c, index) => (
            <div key={index} className="flex flex-col">
              <div className="w-1/1 my-[2px] mt-0 text-lg py-2 border-b bg-blue-300 text-center">
                {c.name}
              </div>
              {c.type === 'information' && (
                <div className="flex flex-col">
                  {c.fields.map((field, i) => (
                    <div key={i} className="w-[100%] mt-[2px] text-lg py-2 border-b">
                      <div className="w-[95%] px-5 mx-auto flex items-center">
                        <div className="w-[25%]">{field.topic}</div>
                        <div className="w-[25%] text-base">{field.description}</div>
                        <div className="w-[25%] text-base px-2">
                          <textarea
                            className="w-[100%] border rounded-lg p-1"
                            value={
                              userData?.filter((fieldValue) => fieldValue.id === field.id)[0]
                                ?.answer || ''
                            }
                          ></textarea>
                        </div>
                        <div className="w-[25%] text-base ">
                          <textarea
                            className="w-[100%] border rounded-lg p-1"
                            value={
                              userData?.filter((fieldValue) => fieldValue.id === field.id)[0]
                                ?.comment || ''
                            }
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {c.type === 'standard-transactions' && (
                <div className="flex flex-col">
                  {c.fields.map((field, ii) => (
                    <div key={ii} className="w-[100%] mt-[2px] text-lg py-2 border-b">
                      <div className="w-[95%] px-5 mx-auto flex items-center">
                        <div className="w-[25%]">{field.topic}</div>
                        <div className="w-[25%] text-base">{field.description}</div>
                        <div className="w-[25%] text-base px-2">
                          <textarea
                            className="w-[100%] border rounded-lg p-1"
                            placeholder="Transaction amount"
                            value={
                              userData?.filter((fieldValue) => fieldValue.id === field.id)[0]
                                ?.transaction_amount || ''
                            }
                          ></textarea>
                        </div>
                        <div className="w-[25%] text-base">
                          <textarea
                            className="w-[100%] border rounded-lg p-1"
                            value={
                              userData?.filter((fieldValue) => fieldValue.id === field.id)[0]
                                ?.comment || ''
                            }
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {c.type === 'individual-transactions' && (
                <div className="flex flex-col">
                  {c.fields.map((field, ii) => (
                    <div key={ii} className="w-[100%] mt-[2px] text-lg py-2 border-b">
                      <div className="w-[95%] px-5 mx-auto flex items-center">
                        <div className="w-[25%]">{field.topic}</div>
                        <div className="w-[25%] text-base">
                          <textarea
                            className="w-[100%] border rounded-lg p-1"
                            placeholder="Transaction partner"
                            value={
                              userData?.filter((fieldValue) => fieldValue.id === field.id)[0]
                                ?.transaction_partner || ''
                            }
                          ></textarea>
                        </div>
                        <div className="w-[25%] px-2 text-base">
                          <textarea
                            className="w-[100%] border rounded-lg p-1"
                            placeholder="Transaction amount"
                            value={
                              userData?.filter((fieldValue) => fieldValue.id === field.id)[0]
                                ?.transaction_amount || ''
                            }
                          ></textarea>
                        </div>
                        <div className="w-[25%] text-base">
                          <textarea
                            className="w-[100%] border rounded-lg p-1"
                            value={
                              userData?.filter((fieldValue) => fieldValue.id === field.id)[0]
                                ?.comment || ''
                            }
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {c.type === 'numbers' && (
                <div className="flex flex-col">
                  {c.fields.map((field, ii) => (
                    <div key={ii} className="w-[100%] mt-[2px] text-lg py-2 border-b">
                      <div className="w-[95%] px-5 mx-auto flex items-center">
                        <div className="w-[25%]">{field.topic}</div>
                        <div className="w-[25%] text-base">{field.description}</div>
                        <div className="w-[25%] text-base px-2">
                          <textarea
                            className="w-[100%] border rounded-lg p-1"
                            placeholder="Transaction amount"
                            value={
                              userData?.filter((fieldValue) => fieldValue.id === field.id)[0]
                                ?.transaction_amount || ''
                            }
                          ></textarea>
                        </div>
                        <div className="w-[25%] text-base">
                          <textarea
                            className="w-[100%] border rounded-lg p-1"
                            value={
                              userData?.filter((fieldValue) => fieldValue.id === field.id)[0]
                                ?.comment || ''
                            }
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        {/* <div className='flex w-[85%] mx-auto mt-4'>
                    <div className='mx-auto w-[75%]'>
                        <button className='w-[100%] p-2 bg-blue-500 text-white text-xl disabled:opacity-60' onClick={handleSaveChanges}>Save Changes</button>
                    </div>
                    <div className='w-[25%] text-blue-500 flex mx-4'>
                        <button className='w-1/2 py-1 border border-blue-500 flex justify-center mx-1 transition-all hover:bg-blue-500 hover:text-white' onClick={previousRow}><ChevronLeftIcon className='w-8 h-8' /></button>
                        <button className='w-1/2 py-1 border border-blue-500 flex justify-center mx-1 transition-all hover:bg-blue-500 hover:text-white' onClick={nextRow}><ChevronRightIcon className='w-8 h-8' /></button>
                    </div>
                </div> */}
      </div>
    </>
  );
}

export default ImportCsv;
